import type { Catalog } from "@schema/catalog.ts";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Toast } from "@/components/ui/Toast.tsx";
import { cachedCatalog, loadCatalog, replaceCachedCatalog } from "./index.ts";
import { useCatalogUpdate } from "./useCatalogUpdate.ts";

const CatalogContext = createContext<Catalog | null>(null);

/**
 * Fetches + validates the catalog once and provides it. Children only render
 * once the catalog exists, so useCatalog() below is always non-null.
 */
export function CatalogProvider({ children }: { children: ReactNode }) {
  const [catalog, setCatalog] = useState<Catalog | null>(cachedCatalog());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (catalog) return;
    let live = true;
    loadCatalog()
      .then((c) => live && setCatalog(c))
      .catch((err: unknown) => live && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [catalog]);

  if (error) {
    return (
      <div role="alert" className="catalog-fallback">
        <h1>The road is closed</h1>
        <p>The catalog failed to load: {error}</p>
        <p>Reload the page to try again.</p>
      </div>
    );
  }
  if (!catalog) {
    return (
      <div className="catalog-fallback" aria-busy="true">
        <p>Loading the road…</p>
      </div>
    );
  }
  return (
    <CatalogContext.Provider value={catalog}>
      {children}
      <CatalogUpdateNotice onUpdated={setCatalog} />
    </CatalogContext.Provider>
  );
}

/**
 * Rendered inside the provider so swapping the catalog re-renders the whole
 * tree through the same path a first load takes — the road, the filters, the
 * plan and the countdown all derive from context, so none of them need to know
 * an update happened.
 */
function CatalogUpdateNotice({ onUpdated }: { onUpdated: (catalog: Catalog) => void }) {
  const handleUpdated = useCallback(
    (next: Catalog) => {
      // Keep the module-level cache in step, or a later `cachedCatalog()`
      // would hand out the version we just replaced.
      replaceCachedCatalog(next);
      onUpdated(next);
    },
    [onUpdated],
  );

  const { state, apply, dismiss } = useCatalogUpdate(handleUpdated);
  if (state === "idle") return null;

  return (
    <Toast
      onDismiss={dismiss}
      action={state === "failed" ? undefined : apply}
      actionLabel="Update"
      busy={state === "applying"}
    >
      {state === "failed"
        ? "That update couldn't be fetched. It'll be offered again next time you load the page."
        : "A newer catalog is published: new titles, or curation that changed."}
    </Toast>
  );
}

export function useCatalog(): Catalog {
  const catalog = useContext(CatalogContext);
  if (!catalog) throw new Error("useCatalog must be used inside CatalogProvider");
  return catalog;
}
